/**
 * What a board's automation rules are written against: its own columns, the
 * columns a rule can watch (on a record board, the record's fields as well),
 * and its groups. Derived from `tracker.layout`, so the automations dialog and
 * the row's activity read rules against the same board.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Tracker } from '@/entities/tracker';

import { recordTriggerColumns, type TriggerColumn } from './automation-model';
import type { CustomColumn, CustomColumnType, StatusOption } from './useTrackersBoard';

export interface AutomationBoard {
  columns: CustomColumn[];
  triggerColumns: TriggerColumn[];
  customGroups: StatusOption[];
}

export function useAutomationBoard(
  tracker: Pick<Tracker, 'itemSource' | 'layout'> | undefined,
): AutomationBoard {
  const { i18n } = useTranslation('trackers');
  const layout = tracker?.layout;

  const columns = useMemo<CustomColumn[]>(
    () =>
      (layout?.customColumns ?? []).flatMap((c) =>
        // Layout jsonb can hold partially-written columns at runtime despite the
        // non-optional DTO types; keep the guard.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        c.id && c.label && c.type
          ? [
              {
                id: c.id,
                label: c.label,
                type: c.type as CustomColumnType,
                statusOptions:
                  c.type === 'status' && Array.isArray(c.config?.options)
                    ? (c.config.options as StatusOption[])
                    : undefined,
              },
            ]
          : [],
      ),
    [layout],
  );
  const triggerColumns = useMemo(
    () => [...columns, ...recordTriggerColumns(tracker?.itemSource, layout?.columnLabels)],
    // The record fields' labels are resolved here, so a locale switch re-reads them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, tracker?.itemSource, layout?.columnLabels, i18n.language],
  );
  const customGroups = useMemo<StatusOption[]>(
    () => (layout?.customGroups ?? []) as StatusOption[],
    [layout],
  );

  return { columns, triggerColumns, customGroups };
}
